import * as React from 'react';
import { Component } from 'react';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import get from 'lodash/get';
import { propStyles } from '../utils/propStyles';
import { LinkFilter } from '../models/configuration/LinkFilter';
import { replacePlaceholders } from '../utils/replacePlaceholders';
import { fundListFilter } from '../utils/fundListFilter';
import normalizeQueryResults from '../utils/query/normalizeQueryResults';
import pivotQueryCollection from '../utils/query/pivotQueryCollection';
import convertRelationToQueryOptions from '../utils/query/convertRelationToQueryOptions';
import DynamicLinkProps from '../models/components/DynamicLinkProps';
import getConditionalData from '../utils/query/getConditionalData';
import getConditionalDataFlag from '../utils/query/getConditionalDataFlag';
import { isNullOrUndefined } from '@kurtosys/ksys-app-template/dist/utils/typeChecks';
import { IQueryKeyValue } from '@kurtosys/ksys-api-client/dist/models/commonTypes';
import { LinkTarget } from '../models/configuration/LinkTarget';

const Link = styled<any, any>('a', 'link', 'dynamicLink')`
    ${propStyles};
`;

@inject('dataStore', 'accessibilityStore', 'queryStore', 'entityStore')
@observer
class DynamicLink extends Component<DynamicLinkProps, any> {
    static defaultProps = {
        color: '#212529',
    };

    @computed
    get disabledChildren() {
        return this.props.disabledChildren;
    }

    @computed
    get link(): string | LinkFilter {
        const { config, dataStore, entityStore } = this.props;
        if (config && entityStore?.value && dataStore) {
            const { linkProperty, url } = config;
            let urlTemplate: string | LinkFilter;
            // Try get url template from a specific property
            if (linkProperty) {
                urlTemplate = get(
                    entityStore.value,
                    `properties_pub.${linkProperty}.value`,
                    ''
                );
            }

            // Fallback to get url template using the lookup to the links config
            if (!urlTemplate && dataStore.config.selectedModule.config.links) {
                let reference;
                if (typeof url === 'string') {
                    reference = url;
                } else if (url && url.url) {
                    reference = url.url;
                }
                if (reference) {
                    urlTemplate =
                        dataStore.config.selectedModule.config.links[reference];
                }
            }

            // Fallback to use the url template straight from the config url property
            if (!urlTemplate) {
                urlTemplate = url;
            }
            return urlTemplate;
        }
        return;
    }

    @computed
    get linkUrl(): string {
        const { config, entityStore } = this.props;
        if (config && entityStore?.value) {
            const link: string | LinkFilter = this.link;

            // if object is LinkFilter then get url from LinkFilter else use it as is
            if (link) {
                let url = '';
                if (typeof link === 'string') {
                    url = link;
                } else if (link.url) {
                    url = link.url;
                }

                const prefixes = ['http://', 'https://'];
                if (
                    !prefixes.find(prefix => url.startsWith(prefix)) &&
                    typeof link !== 'string' &&
                    link.preserveOrigin
                ) {
                    const origin = `${window.location.origin}${window.location.pathname}`;
                    if (url.startsWith('/') && origin.endsWith('/')) {
                        url = url.substr(1);
                    }
                    const insertSlash =
                        !url.startsWith('/') && !origin.endsWith('/');
                    url = `${origin}${insertSlash ? '/' : ''}${url}`;
                }

                return url;
            }

            return;
        }
        return;
    }

    linkQueries(): IQueryKeyValue {
        const { config, entityStore } = this.props;
        if (config && entityStore?.value) {
            const linkFilter = this.link as LinkFilter;
            if (linkFilter && linkFilter.queries) {
                const queryOptions = convertRelationToQueryOptions(
                    linkFilter.queries
                );
                return pivotQueryCollection(queryOptions);
            }

            return;
        }
        return;
    }

    @computed
    get queryContext() {
        const { entityStore, queryContext } = this.props;
        // use passed in context if provided else default to injected entity store context
        return queryContext || entityStore.queryContext;
    }

    @computed
    get linkQueryResults() {
        const { queryStore, fundManager } = this.props;
        const linkQueries = this.linkQueries();
        const executionOptions = queryStore.getExecutionOptionsUsingContext(
            this.queryContext
        );
        const workingExecutionOptions = {
            ...executionOptions,
            fundManagerContext: fundManager,
        };
        return queryStore.executeQueryMime(
            linkQueries,
            workingExecutionOptions
        );
    }

    @computed
    get checkEnableByFundList() {
        const { dataStore, entityStore } = this.props;
        const link = this.link as LinkFilter;
        if (!isNullOrUndefined(link)) {
            const { fundListCheck } = link;
            if (fundListCheck) {
                return dataStore.fundListContainsEntity(
                    entityStore?.value as any,
                    fundListCheck
                );
            }
        }
        return true;
    }

    @computed
    get linkDisabled() {
        const { config, entityStore } = this.props;
        if (config && entityStore?.value) {
            const link = this.link as LinkFilter;
            if (!this.checkEnableByFundList) {
                return true;
            }
            if (link && link.disabledFlag) {
                const data = normalizeQueryResults({
                    ...entityStore.value.properties_pub,
                    ...getConditionalData(entityStore.value, link.disabledFlag),
                });
                return getConditionalDataFlag(data, link.disabledFlag);
            }
        }
        return false;
    }

    @computed
    get target(): LinkTarget | undefined {
        const link: string | LinkFilter = this.link;
        if (link) {
            if (typeof link === 'string') {
                return '_self';
            }
            if (link.target) {
                return link.target;
            }
        }

        return;
    }

    @computed
    get href() {
        const { entityStore, dataStore } = this.props;
        const data = normalizeQueryResults({
            ...entityStore?.value?.properties_pub,
            ...this.linkQueryResults,
        });
        const parsedLink = replacePlaceholders({
            values: data,
            text: this.linkUrl,
            appParams: dataStore.appDataProps,
        });
        return parsedLink;
    }

    @computed
    get canDisplayLink() {
        const { dataStore, entityStore, config, showLink = true } = this.props;
        return (
            showLink &&
            !this.linkDisabled &&
            fundListFilter(config as any, dataStore, entityStore?.value)
        );
    }

    render() {
        const {
            children,
            showLink,
            config,
            dataStore,
            entityStore,
            metricContext,
            ariaLabelKey,
            ariaLabelReplacements,
            accessibilityStore,
            onClick,
            ...aLinkProps
        } = this.props;

        if (this.canDisplayLink) {
            return (
                <Link
                    href={this.href}
                    target={this.target}
                    onClick={e => {
                        e.stopPropagation();
                        if (onClick) {
                            onClick(this.href);
                        }
                    }}
                    data-metric-id={`dynamicLink-${metricContext}`}
                    aria-label={accessibilityStore.linkAccessibilityValue(
                        ariaLabelKey,
                        ariaLabelReplacements
                    )}
                    {...aLinkProps}
                >
                    {children}
                </Link>
            );
        }

        if (this.disabledChildren !== undefined) {
            return this.disabledChildren;
        }

        return children;
    }
}

export default withTheme(DynamicLink) as any;



// WEBPACK FOOTER //
// ./src/Components/DynamicLink.tsx